import { getMessage } from 'util/locale';
import { compact, get, pickBy, size } from 'lodash';
import { createSelector } from 'reselect';

// selectors
import { getAffiliateById } from 'affiliates/selectors';
import { getCropById, getCropUuidByLegacyId, getCropDfpUuidByLegacyId } from 'crops/selectors';
import { getImageUuidsOfLayerForField } from 'imagery/selectors';
import { hasPlantingPlanSubstanceType } from 'planting-plan-form/selectors';
import {
  getFertilizerProductById, getApplicationProductById
} from 'products/fertility-selectors';
import { getSeedProductById } from 'products/planting-selectors';
import { getSoilCompositionForZone } from 'scripts/zones/selectors';
import { getDefaultScriptType } from 'settings/selectors';
import { isSsurgoAllowed } from 'user/selectors';
import { getGeneratedZones } from 'manual/selectors';
import { getActiveCropId } from './currentSelectors';

// constants
import {
  drawingStates,
  exportTypes,
  MAX_ALLOWED_HYBRIDS,
  routeActions,
  zoneSelectionModes,
  NITROGEN_APPLICATION_RATE_TYPES
} from 'constants/values';
import {
  applicationTypes,
  scriptTypes
} from 'constants/scripts';
import {
  overlayLayers,
  seasonlessOverlayLayers,
  datelessOverlayLayers
} from 'constants/imagery';

// utils
import { formatDateToCalendarYear } from 'util/formatters/dateFormatter';
import { determineActualUserId } from 'util/env';
import { logError } from 'api/errors';
import { getScriptTypeForScript } from '../scripts/selectors';

// Note: Some cross-pollination with outside state from here

// TODO: values in uiwidgets are here too...?
export const getActiveCropUuid = (state) => getCropUuidByLegacyId(state, getActiveCropId(state));
export const getActiveCropDfpUuid = (state) => getCropDfpUuidByLegacyId(state, getActiveCropId(state));
export const getActiveOperationUuid = (state) => get(state, 'current.operationUuid');
export const getActiveFieldUuid = (state) => get(state, 'current.fieldUuid');
export const getActiveFieldId = (state) => get(state, 'current.fieldId');
export const getActiveProductYear = (state) => get(state, 'current.productYear', formatDateToCalendarYear());
export const getActiveField = (state) => get(state, `fields.byUuid.${getActiveFieldUuid(state)}`);
export const isFieldFecthed = (state) => state.preload?.fields === 'fetched';
export const getActiveFieldCountryCode = createSelector(getActiveField, (field) => field.countryCode);
export const getActiveFarm = (state) => {
  const activeField = getActiveField(state);
  return get(state, `farms.${get(activeField, 'farmId')}`);
};
export const getActiveOrganizationIdForAffiliateId = (state, affiliateId) => {
  const { organizations } = getAffiliateById(state, affiliateId);
  // if there's only one organization, use that as the default
  return organizations && organizations.length === 1
    ? organizations[0].id
    : get(state, `current.affiliateOrganizationId`);
};
export const getCurrentMapZoom = (state) => get(state, 'current.mapZoom');

// Trial Placements
export const getCurrentTrialPlacmentsScriptId = (state) => get(state, 'current.selectedTrialPlacementId');
export const getActiveTrialPlacementsScript = (state) =>
  get(state, `trialPlacementOptions[${getCurrentTrialPlacmentsScriptId(state)}]`);

export const getActiveScriptId = (state) => get(state, 'current.scriptId');
export const getActiveScript = (state) => get(state, `scripts[${getActiveScriptId(state)}]`);
export const getActiveScriptType = (state) => {
  const activeScript = getActiveScript(state);
  return state?.current?.scriptType
    || (activeScript && getScriptTypeForScript(activeScript))
    || getDefaultScriptType(state);
};
export const getActiveScriptZoneById = (state, zoneId) => getActiveScript(state).zones[zoneId];
export const getActiveScriptWithZonesProperties = createSelector(
  state => getActiveScript(state),
  state => getGeneratedZones(state),
  (activeScript, generatedZones) => {
    const newActiveScript = structuredClone(activeScript);
    Object.values(newActiveScript.zones).forEach((zone) => {
      const generatedZone = Object.values(generatedZones).find(({ meta }) => meta.index === zone.meta.index);

      if (generatedZone) {
        zone.geoJson.properties = generatedZone.geoJson.properties;
      }
    });
    return newActiveScript;
  }
);
export const getActiveUserId = (state) => get(state, 'current.userId');
export const getActiveRoute = (state) => {
  const path = get(state, 'routing.locationBeforeTransitions.pathname');
  return path.substring(1, path.indexOf('/', 1));
};
export const isImpersonating = (state) => {
  return Number(getActiveUserId(state)) !== Number(determineActualUserId());
};

// TODO: Move uiwidgets to its own directory and own selectors

export const getDeleteModal = (state) => get(state, 'uiwidgets.deleteModal');
export const getDuplicateModal = (state) => get(state, 'uiwidgets.duplicateModal');
export const getRenameModal = (state) => get(state, 'uiwidgets.renameModal');
export const getConfirmModal = (state) => get(state, 'uiwidgets.confirmModal');
export const getSelectAffiliateOrganizationModal = (state) => get(state, 'uiwidgets.selectAffiliateOrganizationModal');
export const getSetPrimaryModal = (state) => get(state, 'uiwidgets.setPrimaryModal');

export const getExportType = (state) => {
  return get(state, `uiwidgets.exportOptionsIsOpen.exportType`);
};
export const getExportOptionsIsOpen = (state, exportTypeForLocation) => {
  const { exportType, status } = get(state, `uiwidgets.exportOptionsIsOpen`);
  return exportType === exportTypeForLocation && status;
};
export const bulkExportOptionsIsOpen = (state) => {
  return getExportOptionsIsOpen(state, exportTypes.BULK);
};

export const activeRouteIsEdit = (state) => getActiveRoute(state) === routeActions.EDIT;
export const activeRouteIsManage = (state) => getActiveRoute(state) === routeActions.MANAGE;
export const scriptCreatorExportOptionsIsOpen = (state) => {
  return getExportOptionsIsOpen(state, exportTypes.SINGLE) && activeRouteIsEdit(state);
};
export const seedProductSelectorIsOpen = (state) => {
  return !!get(state, 'uiwidgets.seedProductSelectorModal');
};
export const scriptOptionsIsOpenForScriptId = (state, scriptId) => {
  return !!get(state, `uiwidgets.scriptOptionsIsOpen.${scriptId}`);
};
export const moreDetailsIsOpenForScriptId = (state, scriptId) => {
  return !!get(state, `uiwidgets.moreDetailsIsOpen.${scriptId}`);
};

export const getDrawingState = (state) => get(state, `uiwidgets.drawing.drawingState`);
export const getLastShapeDrawn = (state) => get(state, `uiwidgets.drawing.lastShapeDrawn`);
export const getZoneSelectionMode = (state) => {
  const drawingState = getDrawingState(state);
  switch (drawingState) {
    case drawingStates.MERGE_ZONE_SELECTION: return zoneSelectionModes.MULTI;
    case drawingStates.SPLIT_ZONE_SELECTION:
    case drawingStates.NONE: return zoneSelectionModes.SINGLE;
    default: return zoneSelectionModes.DISABLED;
  }
};
export const isZoneEditingDisabled = (state) => getDrawingState(state) !== drawingStates.NONE;

// Controls for overlay modals and menus
export const isOverlayActive = (state) => !!get(state, `uiwidgets.isOverlayActive`);
export const isOverlayQuickSelectActive = (state) => !!get(state, `uiwidgets.isOverlayQuickSelectActive`);
export const isOverlaySelectModalActive = (state) => !!get(state, `uiwidgets.isOverlaySelectModalActive`);

// What is being selected in the overlay modals and menus - might be canceled, so not Active
export const getSelectedOverlayUuid = (state) => get(state, `uiwidgets.selectedOverlayUuid`);
export const getSelectedOverlaySeason = (state) => get(state, `uiwidgets.selectedOverlaySeason`);
export const getSelectedOverlayLayerGroup = (state) => get(state, `uiwidgets.selectedOverlayLayerGroup`);
export const getSelectedOverlayLayer = (state) => get(state, `uiwidgets.selectedOverlayLayer`);
export const showsOverlaySeasons = (state) => {
  const layer = get(state, `uiwidgets.selectedOverlayLayer`);
  const layerTypeHasSeasons = !seasonlessOverlayLayers[layer];
  return (!!layer && layerTypeHasSeasons);
};
export const showsOverlayDates = (state) => {
  const layer = get(state, `uiwidgets.selectedOverlayLayer`);
  const layerTypeHasDates = !datelessOverlayLayers[layer];
  return (!!layer && layerTypeHasDates);
};

// What has been fully agreed to for current overlay
export const getActiveOverlayLayer = (state) => get(state, `current.overlayLayer`);
export const getActiveOverlayUuid = (state) => get(state, `current.overlayUuid`);
export const getActiveOverlayDate = (state) => get(state, `current.overlayDate`);
export const getActiveOverlayTitle = (state) => {
  const scriptType = getActiveScriptType(state);
  const title = getMessage(`label-text__overlay-layer--${getActiveOverlayLayer(state)}`, { scriptType });
  let date = getActiveOverlayDate(state);
  date = date ? ` - ${formatDateToCalendarYear(date)}` : '';
  return `${title}${date}`;
};

export const getAllActiveOverlayData = (state) => {
  const fieldId = getActiveFieldId(state);
  const layer = getActiveOverlayLayer(state);
  switch (layer) {
    case overlayLayers.PRODUCT_RATE:
      return getActiveScript(state);
    default:
      return get(state, `imagery.${fieldId}.${layer}`); // reasonable default
  }
};

export const getActiveOverlayDataForUuid = (state, uuid) => {
  const allDays = getAllActiveOverlayData(state) || {};
  if (uuid) {
    return allDays[uuid];
  }
  return allDays[Object.keys(allDays).sort().reverse()[0]]; // Take first
};

export const getActiveOverlayData = (state) => { // NOSONAR False-Positive Switch Statement exception
  const layer = getActiveOverlayLayer(state);
  switch (layer) {
    case overlayLayers.PRODUCT_RATE:
      return getActiveScript(state);
    case overlayLayers.SOIL:
      return getActiveOverlayDataForUuid(state);
    case overlayLayers.ACTUAL_RATE:
    case overlayLayers.COMBINE_SPEED:
    case overlayLayers.ELEVATION:
    case overlayLayers.HYBRID:
    case overlayLayers.LIQUID:
    case overlayLayers.MOISTURE:
    case overlayLayers.OTHER:
    case overlayLayers.PLANTER_SPEED:
    case overlayLayers.SCOUTING:
    case overlayLayers.SOIL_SAMPLE_CEC:
    case overlayLayers.SOIL_SAMPLE_EC:
    case overlayLayers.SOIL_SAMPLE_K:
    case overlayLayers.SOIL_SAMPLE_OM:
    case overlayLayers.SOIL_SAMPLE_P:
    case overlayLayers.SOIL_SAMPLE_PH:
    case overlayLayers.TRUE_COLOR:
    case overlayLayers.VEGETATION:
    case overlayLayers.YIELD:
      const uuid = getActiveOverlayUuid(state);
      return getActiveOverlayDataForUuid(state, uuid);
    default:
      logError({ message: `unknown overlay layer: ${layer}` });
      return null;
  }
};

export const getOverlayUuidGivenOffsetFromActive = (state, offset) => {
  if (!showsOverlayDates(state)) {
    return null;
  }
  const fieldId = getActiveFieldId(state);
  const layer = getActiveOverlayLayer(state);
  if (!layer) {
    return null;
  }
  const all = getImageUuidsOfLayerForField({ state, layer, fieldId });
  const current = getActiveOverlayUuid(state);
  if (!current) {
    return null;
  }
  const index = all.indexOf(current);
  const position = index + offset;
  return all[position]; // May be undefined
};

export const getProductInputs = (
  state,
  applicationType
) => get(state, `uiwidgets.productInputs.${applicationType}`, {});
export const getSeedProductInputBySelectId = (state, selectId) =>
  getProductInputs(state, applicationTypes.SEED)[selectId];
export const getApplicationProductInputBySelectId = (state, selectId) =>
  getProductInputs(state, applicationTypes.APPLICATION)[selectId];
export const getActiveSeedProductInputs = (
  state
) => pickBy(getProductInputs(state, applicationTypes.SEED), ({ active }) => active);
export const getActiveApplicationProductInputs = (
  state
) => pickBy(getProductInputs(state, applicationTypes.APPLICATION), ({ active }) => active);
export const getActiveFertilizerProductInputs = (
  state
) => pickBy(getProductInputs(state, applicationTypes.FERTILIZER), ({ active }) => active);
export const getActiveSeedProductIds = (
  state
) => compact(Object.values(getActiveSeedProductInputs(state)).map(({ currentProductId }) => currentProductId));
export const getActiveApplicationProductIds = (
  state
) => compact(Object.values(getActiveApplicationProductInputs(state)).map(({ currentProductId }) => currentProductId));
export const getActiveFertilizerProductIds = (
  state
) => compact(Object.values(getActiveFertilizerProductInputs(state)).map(({ currentProductId }) => currentProductId));
export const getNumActiveSeedProductIds = (state) => getActiveSeedProductIds(state).length;
export const getActiveSeedProducts = (state) => {
  return getActiveSeedProductIds(state).map((productId) => getSeedProductById(state, productId));
};
export const canChooseSeedProduct = (state) => getActiveSeedProductIds(state).length > 1;
export const shouldMakeSeedProductDefault = (state) => getActiveSeedProductIds(state).length === 1;
export const usingLiquidIsChecked = (state) => !!get(state, 'uiwidgets.isUsingLiquid');
export const getNumActiveSeedProductInputs = (state) => size(getActiveSeedProductInputs(state));
export const canAddMoreSeedProductInputs = (state) => {
  const numActiveSeedProductInputs = getNumActiveSeedProductInputs(state);
  const hasAllowedNumOfSeedProductInputs = numActiveSeedProductInputs < MAX_ALLOWED_HYBRIDS;
  return !!getNumActiveSeedProductIds(state) && hasAllowedNumOfSeedProductInputs;
};
export const hasMaxNumOfSeedProductInputs = (state) => getNumActiveSeedProductInputs(state) === MAX_ALLOWED_HYBRIDS;
export const getZonesForActiveScript = (state) => get(getActiveScript(state), `zones`);
export const getSelectedZoneIds = (state) => get(state, 'uiwidgets.selectedZones', []);
export const getActiveZoneId = (state) => get(state, 'current.zoneId');
export const getActiveCrop = (state) => getCropById(state, getActiveCropId(state));
export const getActiveFieldCentroid = (state) => get(state, 'current.fieldCentroid', {});

export const getQuitFlowModal = (state) => get(state, 'uiwidgets.quitFlowModal');
export const getActivePreferredDevice = (state) => get(state, 'uiwidgets.preferredDeviceModal.preferredDevice');
export const getActiveMeasurementType = (state) => get(state, 'uiwidgets.preferredDeviceModal.measurementType');
export const getActiveMeasurementDistance = (state) => get(state, 'uiwidgets.preferredDeviceModal.measurementDistance');
export const preferredDeviceModalIsOpen = (state) => !!getActivePreferredDevice(state);
export const getPreferredDeviceModalExportType = (state) => get(state, 'uiwidgets.preferredDeviceModal.exportType');

export const getConnectToAffiliateModal = (state) => get(state, 'uiwidgets.connectToAffiliateModal');

export const getFatalError = (state) => get(state, 'current.fatalError');
export const zoneSoilsAreVisible = (state, zoneId) => !!get(state, `uiwidgets.zoneSoils.${zoneId}`);
export const getSoilsListForZoneId = (state, zoneId) => {
  if (!isSsurgoAllowed(state)) {
    return [];
  }
  let soils = getSoilCompositionForZone(getActiveScriptZoneById(state, zoneId));
  if (!zoneSoilsAreVisible(state, zoneId)) {
    soils = soils.slice(0, 1);
  }
  return soils;
};
export const getActiveKeypad = (state) => get(state, 'uiwidgets.activeKeypad', {});
export const getActiveProductRateLegend = (state) => get(state, 'uiwidgets.productRateLegend');
export const shouldScrollToSelectedZone = (state) => {
  return get(state, 'uiwidgets.mapWasClickedOnZoneSelect')
    && !!getSelectedZoneIds(state).length
    && getDrawingState(state) === drawingStates.NONE;
};
export const scriptIsFetching = (state) => get(state, 'current.scriptIsFetching');
export const getActiveScriptMethod = (state) => get(state, 'current.scriptMethod');
export const showScriptNameInput = (state) => {
  const scriptType = getActiveScriptType(state);
  if (scriptType === scriptTypes.FERTILITY) {
    return hasPlantingPlanSubstanceType(state);
  }
  return true;
};
export const getActiveScriptConfigView = (state) => get(state, 'uiwidgets.scriptConfigView');
export const getActiveSubstanceTypeUuid = (state) => get(state, 'uiwidgets.substanceTypeUuid', null);
export const hasNitrapyrin = (state) => !!get(state, 'uiwidgets.hasNitrapyrin');
export const getActiveFertilizerProducts = (state) => {
  return getActiveFertilizerProductIds(state).map((productId) => getFertilizerProductById(state, productId));
};
export const getActiveApplicationProducts = (state) => {
  return getActiveApplicationProductIds(state).map((productId) => getApplicationProductById(state, productId));
};
export const getActiveProducts = (state) => {
  switch (getActiveScriptType(state)) {
    case scriptTypes.FERTILITY:
      return getActiveFertilizerProducts(state);
    case scriptTypes.APPLICATION:
      return getActiveApplicationProducts(state);
    case scriptTypes.PLANTING:
      return getActiveSeedProducts(state);
    default:
      return undefined;
  }
};
export const isFieldOwner = (state) => {
  const activeField = getActiveField(state);
  const userId = String(getActiveUserId(state));
  return get(activeField, 'userId', 0).toString() === userId;
};
export const getNitrogenApplicationRateType = (state) =>
  get(state, 'uiwidgets.nitrogenApplicationRateType', NITROGEN_APPLICATION_RATE_TYPES.nitrogen);

export const getSendToDriveOpen = (state) => get(state, 'uiwidgets.isSendToDriveOpen');

export const getSelectedTrialPlacement = (state) => get(state, 'current.selectedTrialPlacementId');

export const isExperimentScripting = (state) => getActiveScriptType(state) === scriptTypes.EXPERIMENT;

export const getCompleteMapLayersId = (state) => get(state, 'manual.completeMapLayers.jobId');
